"use client";

/* eslint-disable @next/next/no-img-element */

import { useAuthorizedImageUrl } from "@/hooks/useAuthorizedImage";
import { cn } from "@/lib/utils";

export type UserAvatarSource = {
  name?: string | null;
  email?: string | null;
  pictureUrl?: string | null;
};

/** Two letters to stand in for a face: first + last initial, else the first two characters. */
export function deriveUserInitials(user: UserAvatarSource): string {
  const source = user.name?.trim() || user.email?.trim() || "";
  if (!source) return "??";

  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  const local = source.includes("@") ? source.split("@")[0] : source;
  return local.slice(0, 2).toUpperCase();
}

export type UserAvatarProps = UserAvatarSource & {
  /** Sizing/shape for the avatar box; applies to both the initials and image forms. */
  className?: string;
};

/**
 * A user's profile picture, or their initials when there isn't one. An uploaded
 * avatar is served from `/api/avatars/...`, which needs a bearer token an `<img>`
 * can't send — the hook fetches those; an identity-provider URL passes through.
 */
export function UserAvatar({ name, email, pictureUrl, className }: UserAvatarProps) {
  const initials = deriveUserInitials({ name, email });
  const { url, state } = useAuthorizedImageUrl(pictureUrl);
  const showImage = Boolean(url) && state === "loaded";

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full border border-border/60 bg-[color-mix(in_oklab,var(--token-surface-default)_15%,transparent)] font-semibold uppercase leading-none tracking-[0.16em] text-foreground",
        className,
      )}
      aria-hidden
    >
      {showImage ? (
        <img src={url ?? undefined} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" />
      ) : (
        <span className="pointer-events-none select-none">{initials}</span>
      )}
    </span>
  );
}
